// The frame: menu rail, top bar and page (prototype/admin/src/10-shell.js sidebar(), topbar(), shell()).
import { Brand } from '@xr/ui';
import { type ReactNode, useEffect, useState } from 'react';
import { L, N, isDark, prefs, toggleLang, toggleTheme } from '../lib/prefs';
import { planName, roleName } from '../lib/names';
import { go, useRoute } from '../lib/route';
import { app, can, openCompany, signOut, useApp } from '../lib/session';
import { Avatar, Btn, IBtn, Ic, Kbd, MenuBtn, view, useView } from '../ui/kit';
import { toast } from '../ui/toast';
import { NAV, pageAllowed } from './nav';
import { soon } from './soon';

function Sidebar() {
  const { co, me, workspaces, brandColor } = useApp();
  const v = useView();
  const r = useRoute();
  if (!co || !me) return null;
  const switchWs = async (id: string) => {
    if (id === co.id) return;
    await openCompany(id);
    go('dashboard');
    const w = app.get().co;
    if (w) toast(L('Switched to ', 'تم التبديل إلى ') + N(w.name_en, w.name_ar));
  };
  return (
    <>
      <aside className={'side ' + (v.navOpen ? 'open' : '')} aria-label={L('Main menu', 'القائمة الرئيسية')}>
        <div className="ws">
          <MenuBtn id="ws" align="start wide" items={[
            ...workspaces.map(w => ({ icon: w.tenant_id === co.id ? 'check' : 'building', onClick: () => void switchWs(w.tenant_id), label: <>{N(w.name_en, w.name_ar)} <small>{planName(w.plan)} · {roleName(w.role)}</small></> })),
            '-',
            { icon: 'plus', label: L('Create a company', 'إنشاء شركة'), disabled: true, why: L('Create a company in the xReceived phone app.', 'أنشئ الشركة من تطبيق xReceived على الهاتف.') },
          ]} trigger={toggle => (
            <button type="button" className="ws-btn" aria-label={L('Switch company', 'تبديل الشركة')} onClick={toggle}>
              <span className="ws-logo" style={{ ['--b' as string]: brandColor }}>{co.short}</span>
              <span className="ws-name"><b>{N(co.name_en, co.name_ar)}</b><small>{planName(co.plan)} · {roleName(me.role)}</small></span>
              <Ic n="chevD" cls="sm" />
            </button>
          )} />
        </div>
        <nav className="nav">
          {NAV.map(g => {
            const items = g.items.filter(([x]) => pageAllowed(x));
            return items.length ? (
              <div className="ng" key={g.g[0]}>
                <div className="ng-h">{L(g.g[0], g.g[1])}{g.staff ? <span className="staff-tag">{L('Staff', 'الفريق')}</span> : null}</div>
                {items.map(([x, icn, en, ar]) => (
                  <a key={x} href={'#' + x} className={'ni ' + (r === x ? 'on' : '')} aria-current={r === x ? 'page' : undefined}
                    onClick={e => { e.preventDefault(); view.set({ navOpen: false, menu: null }); go(x); }}>
                    <Ic n={icn} /><span>{L(en, ar)}</span>
                  </a>
                ))}
              </div>
            ) : null;
          })}
        </nav>
        <div className="side-foot">
          <a href="#help" className={'ni ' + (r === 'help' ? 'on' : '')} onClick={e => { e.preventDefault(); view.set({ navOpen: false }); go('help'); }}><Ic n="help" /><span>{L('Help & support', 'المساعدة والدعم')}</span></a>
          <div className="brandline"><Brand kind="lockup" h={18} /></div>
        </div>
      </aside>
      {v.navOpen ? <div className="side-scrim" onClick={() => view.set({ navOpen: false })} /> : null}
    </>
  );
}

/* Install as a desktop app (PWA), when the browser offers it. */
type InstallEvt = Event & { prompt: () => Promise<void>; userChoice: Promise<{ outcome: string }> };
let installEvt: InstallEvt | null = null;
window.addEventListener('beforeinstallprompt', e => { e.preventDefault(); installEvt = e as InstallEvt; });
const isStandalone = () => { try { return matchMedia('(display-mode: standalone)').matches; } catch { return false; } };
async function install() {
  if (!installEvt) return toast(L('Use your browser\'s menu and choose Install to add the panel to your desktop.', 'من قائمة المتصفح اختر «تثبيت» لإضافة اللوحة إلى سطح المكتب.'), { icon: 'monitor' });
  try { await installEvt.prompt(); const c = await installEvt.userChoice; if (c.outcome !== 'accepted') toast(L('Install cancelled', 'أُلغي التثبيت')); } catch { toast(L('Your browser couldn\'t install the app here.', 'تعذر التثبيت هنا.'), { tone: 'bad' }); }
  installEvt = null;
}

function useOnline() {
  const [on, setOn] = useState(navigator.onLine !== false);
  useEffect(() => {
    const a = () => setOn(true), b = () => setOn(false);
    window.addEventListener('online', a); window.addEventListener('offline', b);
    return () => { window.removeEventListener('online', a); window.removeEventListener('offline', b); };
  }, []);
  return on;
}

function Topbar() {
  const { me } = useApp();
  const v = useView();
  const online = useOnline();
  if (!me) return null;
  const st = online ? 'connected' : 'offline';
  const label = online ? L('Live', 'مباشر') : L('Offline', 'غير متصل');
  return (
    <header className="top">
      <IBtn icon="menu" title={L('Open menu', 'فتح القائمة')} cls="only-narrow" onClick={() => view.set({ navOpen: !v.navOpen })} />
      <button type="button" className="cmdk" aria-label={L('Search or jump to', 'ابحث أو انتقل')} onClick={() => soon(L('Search', 'البحث'))}>
        <Ic n="search" /><span>{L('Search receipts, cheques, people…', 'ابحث في السندات والشيكات والأشخاص…')}</span><Kbd k="⌘K" />
      </button>
      <div className="top-r">
        <MenuBtn id="live" panel={() => <LiveMenu online={online} />} trigger={toggle => (
          <button type="button" className={'live st-' + st} aria-label={label} onClick={toggle}><i /><span>{label}</span></button>
        )} />
        {!isStandalone() ? <button type="button" className="inst-btn" title={L('Install as a desktop app', 'تثبيت كتطبيق سطح مكتب')} onClick={() => void install()}><Ic n="monitor" /><span>{L('Install app', 'تثبيت التطبيق')}</span></button> : null}
        {me.role === 'auditor' ? <span className="ro-chip"><Ic n="eye" />{L('Read-only', 'قراءة فقط')}</span> : null}
        {can('ai') ? <button type="button" className="ai-btn" aria-label={L('Ask the assistant', 'اسأل المساعد')} onClick={() => soon(L('The assistant', 'المساعد'))}><Ic n="spark" /><span>{L('Ask AI', 'اسأل الذكاء')}</span></button> : null}
        {can('issue') ? <Btn kind="primary" icon="plus" cls="hide-narrow" onClick={() => soon(L('Issuing receipts', 'إصدار السندات'))}>{L('New receipt', 'سند جديد')}</Btn> : null}
        <MenuBtn id="notifs" panel={() => <NotifPanel />} trigger={toggle => <IBtn icon="bell" title={L('Notifications', 'الإشعارات')} onClick={toggle} />} />
        <MenuBtn id="me" align="end" items={[
          { icon: 'user', label: L('My account', 'حسابي'), onClick: () => go('account') },
          { icon: 'globe', label: prefs.get().lang === 'ar' ? 'English' : 'العربية', onClick: toggleLang },
          { icon: isDark() ? 'sun' : 'moon', label: isDark() ? L('Light theme', 'الوضع الفاتح') : L('Dark theme', 'الوضع الداكن'), onClick: toggleTheme },
          !isStandalone() && { icon: 'monitor', label: L('Install desktop app', 'تثبيت تطبيق سطح المكتب'), onClick: () => void install() },
          '-',
          { icon: 'logout', label: L('Sign out', 'تسجيل الخروج'), onClick: () => void signOut() },
        ]} trigger={toggle => <button type="button" className="me-btn" aria-label={L('Your account', 'حسابك')} onClick={toggle}><Avatar name={me.name} role={me.role} /></button>} />
      </div>
    </header>
  );
}

function NotifPanel() {
  return (
    <div className="menu notif end" role="dialog" aria-label={L('Notifications', 'الإشعارات')}>
      <div className="np-head"><b>{L('Notifications', 'الإشعارات')}</b></div>
      <p className="muted pad">{L('You\'re all caught up.', 'لا جديد.')}</p>
    </div>
  );
}
function LiveMenu({ online }: { online: boolean }) {
  const st = online ? 'connected' : 'offline';
  return (
    <div className="menu end live-menu">
      <div className="lm-h"><i className={'st-' + st} /><b>{online ? L('Live updates are on', 'التحديثات المباشرة مفعلة') : L('You\'re offline', 'أنت غير متصل')}</b></div>
      <p>{online ? L('New receipts, handovers and approvals appear as soon as phones sync. No need to refresh.', 'تظهر السندات والتسليمات والموافقات فور مزامنة الهواتف. لا حاجة للتحديث.') : L('Changes are paused and nothing is lost; updates arrive when you reconnect.', 'التغييرات متوقفة ولن يضيع شيء.')}</p>
    </div>
  );
}

export function Shell({ children }: { children: ReactNode }) {
  return (
    <div className="shell">
      <Sidebar />
      <div className="main">
        <Topbar />
        <main id="page" className="page" tabIndex={-1}>{children}</main>
      </div>
    </div>
  );
}
